import { type Routes } from '@angular/router';
import { LoginComponent } from './login-feature/login/login.component';
import { ForgotPasswordComponent } from './login-feature/forgot-password/forgot-password.component';
import { RegisterComponent } from './login-feature/register/register.component';
import { AccountSummaryComponent } from './account-summary-feature/account-summary/account-summary.component';
import { MultiAccountSummaryComponent } from './account-summary-feature/multi-account-summary/multi-account-summary.component';
import { PaymentComponent } from './payment-feature/payment/payment.component';
import { SuccessPageComponent } from './shared/components/success-page/success-page.component';
import { ErrorPageComponent } from './shared/components/error-page/error-page.component';
import { CancelFuturePaymentComponent } from './shared/components/cancel-future-payment/cancel-future-payment.component';
import { CreatePasswordComponent } from './login-feature/create-password/create-password.component';
import { AutopayEnrollComponent } from './payment-feature/autopay/autopay-enroll/autopay-enroll.component';
import { AutopayCancelComponent } from './payment-feature/autopay/autopay-cancel/autopay-cancel.component';
import { WalletComponent } from './billing-payments-feature/wallet/wallet.component';
import { RateRenewalComponent } from './manage-account-feature/rate-renewal/rate-renewal.component';
import { OverviewComponent } from './manage-account-feature/overview/overview.component';
import { DeleteAccountComponent } from './manage-account-feature/delete-account/delete-account.component';
import { UpdateLoginInfoComponent } from './manage-account-feature/update-login-info/update-login-info.component';
import { PlanDetailsComponent } from './manage-account-feature/plan-details/plan-details.component';
import { AlertsComponent } from './manage-account-feature/alerts/alerts.component';
import { ReferFriendComponent } from './manage-account-feature/refer-friend/refer-friend.component';
import { StopServiceComponent } from './account-actions-feature/stop-service/stop-service.component';
import { StartServiceComponent } from './account-actions-feature/start-service/service-address/start-service.component';
import { ContactInfoComponent } from './manage-account-feature/contact-info/contact-info.component';
import { ChangeAddressComponent } from './manage-account-feature/change-address/change-address.component';
import { ServiceAgreementsComponent } from './manage-account-feature/service-agreements/service-agreements.component';
import { PayPlanComponent } from './billing-payments-feature/pay-plan/pay-plan.component';
import { HistoryComponent } from './billing-payments-feature/history/history.component';
import { BillDeliveryComponent } from './billing-payments-feature/bill-delivery/bill-delivery.component';
import { RegisterAccountComponent } from './login-feature/register-account/register-account.component';
import { multiAccountResolver } from './shared/resolvers/multi-account.resolver';
import { AccountLookupComponent } from './one-time-payment-feature/account-lookup/account-lookup.component';
// import { OneTimePaymentComponent } from './one-time-payment-feature/one-time-payment/one-time-payment.component';
import { ScheduleServiceComponent } from './account-actions-feature/start-service/schedule-service/schedule-service/schedule-service.component';
import { TechnicalErrorComponent } from './shared/components/technical-error/technical-error.component';
import { UnauthenticatedRateRenewalComponent } from './unauthenticated-rate-renewal-feature/unauthenticated-rate-renewal.component';
import { OutageErrorComponent } from './shared/components/outage-error/outage-error.component';
import { AuthGuard } from './shared/guards/auth.guard';
import { MobileOnlyGuard } from './shared/guards/mobile-only.guard';
import { BioTermsComponent } from './shared/components/bio-terms/bio-terms.component';
import { UnauthenticatedPaperlessComponent } from './unauthenticated-paperless-feature/unauthenticated-paperless.component';
import { AdvantageComponent } from './advantage/advantage.component';
import { deepLinkGuard } from './shared/guards/deep-link.guard';
import { OneTimePaymentPixelComponent } from './one-time-payment-feature/one-time-payment-pixel/one-time-payment-pixel.component';

export const routes: Routes = [
  { path: 'digital-payment-callback', canActivate: [deepLinkGuard], component: LoginComponent },
  { path: 'closed', component: HistoryComponent, resolve: { data: multiAccountResolver }, canActivate: [AuthGuard] },
  {
    path: 'manage-account',
    canActivateChild: [AuthGuard],
    children: [
      {
        path: 'address',
        canActivateChild: [AuthGuard],
        children: [
          { path: 'change', component: ChangeAddressComponent },
          { path: 'add', component: OverviewComponent },
        ],
      },
      { path: 'alerts', component: AlertsComponent },
      { path: 'contact-info', component: ContactInfoComponent },
      { path: 'plan-details', component: PlanDetailsComponent },
      { path: 'rate-renewal', component: RateRenewalComponent },
      { path: 'overview', component: OverviewComponent, resolve: { data: multiAccountResolver } },
      { path: 'delete-account', component: DeleteAccountComponent },
      { path: 'update-login-info', component: UpdateLoginInfoComponent },
      { path: 'refer-friend', component: ReferFriendComponent },
      { path: 'stop-service', component: StopServiceComponent },
      {
        path: 'start-service',
        canActivateChild: [AuthGuard],
        children: [
          { path: 'schedule-service', component: ScheduleServiceComponent },
          { path: 'choose-rates', component: RateRenewalComponent },
          { path: 'start-success', component: SuccessPageComponent },
          { path: '', component: StartServiceComponent },
        ],
      },
      {
        path: 'transfer-service',
        canActivateChild: [AuthGuard],
        children: [
          { path: 'choose-address', component: StartServiceComponent },
          { path: 'schedule-service', component: ScheduleServiceComponent },
          { path: 'choose-rates', component: RateRenewalComponent },
          { path: 'move-success', component: SuccessPageComponent },
          { path: '', component: StopServiceComponent },
        ],
      },
      {
        path: 'reconnect-service',
        canActivateChild: [AuthGuard],
        children: [
          { path: 'schedule-service', component: ScheduleServiceComponent },
          { path: 'reconnect-rates', component: RateRenewalComponent },
          { path: 'reconnect-success', component: SuccessPageComponent },
          { path: 'reconnect-error', component: ErrorPageComponent },
          { path: '', component: ScheduleServiceComponent },
        ],
      },
      { path: 'service-agreements', component: ServiceAgreementsComponent },
      { path: '', component: OverviewComponent, resolve: { data: multiAccountResolver } },
    ],
  },
  {
    path: 'billing-and-payments',
    canActivateChild: [AuthGuard],
    children: [
      { path: 'wallet', component: WalletComponent },
      {
        path: 'pay-plan',
        canActivateChild: [AuthGuard],
        children: [
          { path: 'success', component: SuccessPageComponent },
          { path: 'enroll', component: PayPlanComponent },
          { path: '', component: PayPlanComponent },
        ],
      },
      { path: 'bill-delivery', component: BillDeliveryComponent },
      { path: 'history', component: HistoryComponent, resolve: { data: multiAccountResolver } },
      { path: '', component: HistoryComponent, resolve: { data: multiAccountResolver } },
    ],
  },
  { path: 'autopay-enroll-success', component: SuccessPageComponent, canActivate: [AuthGuard] },
  { path: 'autopay-cancel', component: AutopayCancelComponent, canActivate: [AuthGuard] },
  { path: 'autopay-cancel-change', component: AutopayCancelComponent, canActivate: [AuthGuard] },
  { path: 'autopay-enroll', component: AutopayEnrollComponent, canActivate: [AuthGuard] },
  { path: 'autopay-change', component: AutopayEnrollComponent, canActivate: [AuthGuard] },
  { path: 'payment-success', component: SuccessPageComponent, canActivate: [AuthGuard] },
  { path: 'payment', component: PaymentComponent, resolve: { data: multiAccountResolver }, canActivate: [AuthGuard] },
  { path: 'multiaccount-summary', component: MultiAccountSummaryComponent, canActivate: [AuthGuard] },
  {
    path: 'pending-start',
    component: AccountSummaryComponent,
    resolve: { data: multiAccountResolver },
    canActivate: [AuthGuard],
  },
  { path: 'seasonal', component: HistoryComponent, resolve: { data: multiAccountResolver }, canActivate: [AuthGuard] },
  {
    path: 'account-summary',
    component: AccountSummaryComponent,
    resolve: { data: multiAccountResolver },
    canActivate: [AuthGuard],
  },
  { path: 'register', component: RegisterComponent },
  { path: 'forgot', component: ForgotPasswordComponent },
  { path: 'error-page', component: ErrorPageComponent, canActivate: [AuthGuard] },
  { path: 'cancel-future-payment', component: CancelFuturePaymentComponent, canActivate: [AuthGuard] },
  { path: 'cancel-future-payment-success', component: SuccessPageComponent, canActivate: [AuthGuard] },
  { path: 'authentication/reset', component: CreatePasswordComponent },
  { path: 'rate-change', component: RateRenewalComponent, canActivate: [AuthGuard] },
  { path: 'rate-change-success', component: SuccessPageComponent, canActivate: [AuthGuard] },
  { path: 'register-account', component: RegisterAccountComponent },
  { path: 'stop-success', component: SuccessPageComponent, canActivate: [AuthGuard] },
  { path: 'technical-error', component: TechnicalErrorComponent },
  { path: 'accounts/ratesrenewal', component: UnauthenticatedRateRenewalComponent },
  { path: 'accounts/ratesrenewal/error', component: ErrorPageComponent },
  { path: 'accounts/ratesrenewal/success', component: SuccessPageComponent },
  { path: 'accounts/paperless', component: UnauthenticatedPaperlessComponent },
  { path: 'accounts/paperless/error', component: ErrorPageComponent },
  { path: 'accounts/paperless/success', component: SuccessPageComponent },
  { path: 'biometrics-terms', component: BioTermsComponent, canActivate: [MobileOnlyGuard, AuthGuard] },
  { path: 'advantage', component: AdvantageComponent, canActivate: [AuthGuard] },
  {
    path: 'authentication',
    children: [
      { path: 'accountlookup', component: AccountLookupComponent },
      { path: 'one-time-payment', component: OneTimePaymentPixelComponent },
      { path: 'one-time-payment/success', component: SuccessPageComponent },
      { path: 'one-time-payment/error', component: ErrorPageComponent },
    ],
  },
  { path: '', pathMatch: 'full', redirectTo: 'login' },
  { path: 'login', component: LoginComponent },
  { path: 'app-error', component: ErrorPageComponent },
  { path: 'site-unavailable', component: OutageErrorComponent },
  // Wildcard route: captures deep links before redirecting to login
  // deepLinkGuard stores the destination, then redirects to login
  { path: '**', canActivate: [deepLinkGuard], component: LoginComponent },
];
